// PDF export bridge. POSTs to the `pdf-export` Edge Function and
// hands the resulting bytes back to the writer either as a download
// (exportPdf) or in a new tab (printPreview). The active scriptId is
// read from the active-script module so the renderer can keep
// calling exportPdf(defaultName) with the same signature as Electron.

import { getActiveScriptId } from './active-script.js';

async function callPdfExport({ supabase, env, scriptId, draftId, hasTitlePage }) {
  if (!supabase) return { ok: false, error: 'Supabase not configured' };
  const { data: sess } = await supabase.auth.getSession();
  const jwt = sess?.session?.access_token;
  if (!jwt) return { ok: false, error: 'Sign in to export PDFs', code: 'auth-missing' };
  if (!env.SUPABASE_URL) return { ok: false, error: 'Supabase URL not configured' };

  const url = `${env.SUPABASE_URL.replace(/\/$/, '')}/functions/v1/pdf-export`;
  let res;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${jwt}` },
      body: JSON.stringify({ scriptId, draftId, hasTitlePage }),
    });
  } catch (err) {
    return { ok: false, error: err && err.message ? err.message : 'network failed' };
  }
  if (!res.ok) {
    let parsed = null;
    try {
      parsed = await res.json();
    } catch {
      /* empty */
    }
    return {
      ok: false,
      error: (parsed && (parsed.error || parsed.code)) || `HTTP ${res.status}`,
      code: parsed?.code,
    };
  }
  const blob = await res.blob();
  return { ok: true, blob };
}

function downloadBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function buildPdfHandlers({ supabase, env }) {
  return {
    async exportPdf(defaultName) {
      // Renderer passes a positional string (Electron's preload
      // never wrapped this one). The active scriptId is read from
      // the active-script module — file.js sets it whenever a
      // script loads, mirroring how Electron tracks it in main state.
      const scriptId = getActiveScriptId();
      if (!scriptId) return { ok: false, error: 'no active script', code: 'no-script' };
      const r = await callPdfExport({ supabase, env, scriptId });
      if (!r.ok) return r;
      const name = typeof defaultName === 'string' && defaultName ? defaultName : 'screenplay.pdf';
      downloadBlob(r.blob, name);
      return { ok: true };
    },

    async printPreview() {
      const scriptId = getActiveScriptId();
      if (!scriptId) return { ok: false, error: 'no active script' };
      const r = await callPdfExport({ supabase, env, scriptId });
      if (!r.ok) return r;
      // Inline preview: open the blob URL in a new tab so the user
      // gets the browser's native PDF viewer (with print button, page
      // count, zoom). Avoids shipping pdfjs in the M4 cut.
      const url = URL.createObjectURL(r.blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
      return { ok: true };
    },
  };
}
